// The sheets an action opens (day-view.md §7): "Ödeme al" with its
// amount and channel, "Not düş", the duplicate confirm. Turkish, one line to confirm, nothing to read
// before it can be dismissed. The drawer a tap opens is Drawer.tsx,
// the client form ClientSheet.tsx.

import {useEffect, useRef, useState} from "react";
import type {ReactNode} from "react";
import type {BoardCell, BoardClient, Channel} from "../api/contract";
import {CHANNELS, STATUS_TR, parseTL, payOutcome, payPlan, trNumber} from "../labels";
import {useMoney} from "../ui/blind-context";
import {channelWord} from "./useActions";
import type {ClientRef, DuplicateAsk, PayCollect} from "./useActions";

// ── The sheet itself ──────────────────────────────────────

export function Sheet({title, onClose, children, className, head}: {
  title?: ReactNode; onClose: () => void; children: ReactNode;
  className?: string; // a sheet with a look of its own (the pay sheet)
  head?: ReactNode; // a head of its own in place of title and ×: the seans drawer's
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <section className={className ? `sheet ${className}` : "sheet"} role="dialog" aria-modal="true"
        onClick={(e) => e.stopPropagation()}>
        {head ?? (
          <header className="sheet-head">
            <div className="sheet-title">{title}</div>
            <button className="sheet-close" aria-label="Kapat" onClick={onClose}>×</button>
          </header>
        )}
        <div className="sheet-body">{children}</div>
      </section>
    </div>
  );
}

const cellTitle = (cell: BoardCell, providerName: string) => (
  <>
    <span className="time">{cell.time}–{cell.endTime}</span>{" "}
    {cell.clients.map((c) => c.name).join(", ") || cell.title}
    <span className="muted"> — {providerName}</span>{" "}
    <span className={`chip ${cell.status}`}>
      {STATUS_TR[cell.status].icon} {STATUS_TR[cell.status].word}
    </span>
  </>
);

// ── Seans yapıldı ─────────────────────────────────────────
// A3 (Onur, 13.10.2026): one card. The big figure is what the desk
// takes today and the dark button under it takes it — or, when credit
// pays for the seans, takes it from the credit. "Farklı tutar gir"
// turns the figure itself into the field, all of it selected, so
// another amount is typed where the figure stands and nothing on the
// sheet moves: less leaves the rest open, more stays as credit, and
// the line under the figure says which. The strip under the card is
// the account the figure comes from. A family with credit can leave
// money on top of it ("Ek ödeme al"); one that pays nothing today is
// "Ödemedi, sonra ödeyecek" — both no-money paths record the seans
// without a tahsilat, as before.

// The ways money comes in, the open ones first: POS stays on the row,
// greyed with its reason, while it is off.
const PAY_CHANNELS = [...CHANNELS].sort((a, b) => Number(a.off !== null) - Number(b.off !== null));

export function PaySheet({cell, client, providerName, busy, onClose, onPay}: {
  cell: BoardCell; client: BoardClient; providerName: string; busy: string | null;
  onClose: () => void; onPay: (collect: PayCollect | null) => void;
}) {
  const plan = client.money && !client.recorded ? payPlan(client.money) : null;
  const [editing, setEditing] = useState(false);
  const [typed, setTyped] = useState("");
  const [channel, setChannel] = useState<Channel>("cash");
  const field = useRef<HTMLInputElement>(null);
  // Gizli mod reaches the sheet: every figure is masked, and "Farklı
  // tutar gir" opens an empty field instead of the hidden figure. What
  // the desk types stays as typed.
  const {blind, tl, num} = useMoney();
  useEffect(() => {
    if (editing) {
      field.current?.focus();
      field.current?.select();
    }
  }, [editing]);

  const title = (
    <>
      {client.name}
      <span className="muted"> — {providerName} · {cell.time}–{cell.endTime}
        {cell.isGroup ? " · grup" : ""}</span>
    </>
  );
  if (!plan) {
    return (
      <Sheet className="pay-sheet" title={title} onClose={onClose}>
        <div className="pay-card pay-main">
          {client.recorded ? (
            <p className="pay-note">Bu seans kaydedildi — çekmecedeki Para kartından bakın.</p>
          ) : (
            <p className="error">
              Bu çift için ücret tanımlı değil — sohbette
              <code> nefes_set_client_provider_terms</code> ile tanımlayın, sonra buradan alın.
            </p>
          )}
        </div>
      </Sheet>
    );
  }

  const locked = busy !== null;
  const running = busy === `pay:${client.clientId}`;
  const typedTL = editing ? parseTL(typed) : null;
  // The amount the button takes: today's figure, or the one typed.
  const amount = editing ? typedTL : plan.kind === "take" ? plan.dueTL : null;
  const taking = amount !== null;
  const unreadable = editing && typed.trim() !== "" && typedTL === null;

  const take = () => {
    if (amount === null) return;
    onPay({channel, ...(amount !== plan.feeTL ? {amountTL: amount} : {})});
  };
  // A figure the sheet cannot read never falls through to the no-money
  // path: the cash in hand would go unrecorded.
  const go = unreadable ? null : taking ? take :
    plan.kind === "take" ? null : () => onPay(null);
  const startEditing = () => {
    setTyped(blind || plan.dueTL === 0 ? "" : trNumber(plan.dueTL));
    setEditing(true);
  };

  // The line under the figure: where the account stands at rest, what
  // the typed amount leaves while editing.
  let note: {text: string; tone?: "bad" | "ok"};
  if (!editing) {
    const rest = payOutcome(plan, 0);
    note = plan.kind === "credit" ? {tone: "ok", text: rest.kind === "credit" ?
      `Kredi karşılıyor, ${tl(rest.creditTL)} kalır` : "Kredi tam karşılıyor"} :
      plan.kind === "free" ? {text: "Bu seansın ücreti yok"} :
      plan.earlierTL > 0 ? {text: `${tl(plan.earlierTL)} önceki seanslardan`, tone: "bad"} :
      plan.creditTL > 0 ? {text: `${tl(plan.creditTL)} kredi düşüldü`, tone: "ok"} :
      {text: "Bu seansın ücreti"};
  } else if (unreadable) {
    note = {text: "Okunamadı — örnek: 1.250,50", tone: "bad"};
  } else if (amount === null) {
    note = {text: plan.kind === "credit" ? "Para alınmazsa krediden düşer." :
      plan.kind === "free" ? "Para alınmazsa ücretsiz kaydedilir." : "Tutarı yazın."};
  } else {
    const o = payOutcome(plan, amount);
    const settled = plan.earlierTL > 0 ? "Borç kapanır" : "Seans ödenir";
    note = o.kind === "owe" ? {text: `Kalan ${tl(o.leftTL)} borç olarak durur.`} :
      o.kind === "even" ? {text: plan.earlierTL > 0 ? "Hesap kapanır." : "Seans ödenir."} :
      {text: `${settled}, kredi ${tl(o.creditTL)} olur.`};
  }

  const figure = editing ? typed : num(plan.dueTL);
  // In gizli mod the empty field keeps the masked figure as its
  // placeholder, so it holds its width until the desk types.
  const placeholder = blind ? num(plan.dueTL) : "0";
  const button = running ? "Kaydediliyor…" :
    unreadable ? "Tutarı düzeltin" :
    taking ? `${tl(amount)} ${channelWord(channel)} alındı` :
    plan.kind === "credit" ? <>Krediden düş <span>· {tl(plan.feeTL)}</span></> :
    plan.kind === "free" ? "Seansı kaydet" : "Tutarı yazın";

  return (
    <Sheet className="pay-sheet" title={title} onClose={onClose}>
      <div className="pay-card">
        <div className="pay-main">
          <div className="pay-head">
            <small>{editing ? "Alınan tutar" : "Bugün alınacak"}</small>
            <button className="pay-edit" disabled={locked}
              onClick={() => (editing ? setEditing(false) : startEditing())}>
              {/* Both words hold the place, so the chip keeps its width. */}
              <span className={editing ? "off" : ""} aria-hidden={editing}>Farklı tutar gir</span>
              <span className={editing ? "" : "off"} aria-hidden={!editing}>Vazgeç</span>
            </button>
          </div>
          <div className="pay-figure-wrap">
            <div className={editing ? "pay-figure-row editing" : "pay-figure-row"}>
              <span className="pay-figure" data-value={figure || placeholder}>
                <input ref={field} id="pay-amount" type="text" inputMode="decimal" size={1}
                  autoComplete="off" readOnly={!editing} tabIndex={editing ? 0 : -1}
                  aria-label={editing ? "Alınan tutar (TL)" : "Bugün alınacak (TL)"}
                  placeholder={placeholder} value={figure}
                  onChange={(e) => setTyped(e.target.value)}
                  onBlur={() => typedTL !== null && setTyped(trNumber(typedTL))}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && editing) {
                      // Back to today's figure; a second Escape closes the sheet.
                      e.stopPropagation();
                      setEditing(false);
                    } else if (e.key === "Enter" && taking && go && !locked) {
                      // Enter takes money only; recording with none is the button's.
                      go();
                    }
                  }} />
              </span>
              <span className="pay-cur">TL</span>
            </div>
            <p className={note.tone ? `pay-note ${note.tone}` : "pay-note"} aria-live="polite">
              {note.text}
            </p>
          </div>
          <div className={taking ? "pay-methods" : "pay-methods dim"} role="group"
            aria-label="Ödeme yolu">
            {PAY_CHANNELS.map((ch) => {
              const [word, more] = ch.word.split(" / ");
              return (
                <button key={ch.channel} className={ch.off ? "off" : ""}
                  aria-pressed={taking && channel === ch.channel}
                  disabled={!taking || ch.off !== null || locked} title={ch.off ?? ""}
                  onClick={() => setChannel(ch.channel)}>
                  {word}{more && <span className="pay-wide"> / {more}</span>}
                </button>
              );
            })}
          </div>
          <button className="pay-go" disabled={!go || locked} onClick={() => go?.()}>
            {button}
          </button>
        </div>
        <div className="pay-strip">
          <div><span>Bu seans</span><b>{num(plan.feeTL)}</b></div>
          <div className={plan.earlierTL > 0 ? "neg" : ""}>
            <span>Önceki</span><b>{num(plan.earlierTL)}</b>
          </div>
          <div className={plan.creditTL > 0 ? "pos" : ""}
            title={client.money && client.money.providerCreditTL > 0 ?
              `${tl(client.money.providerCreditTL)} bu danışmana kilitli` : undefined}>
            <span>Kredi</span><b>{num(plan.creditTL)}</b>
          </div>
        </div>
      </div>
      {plan.kind === "take" ? (
        <button className="pay-link" disabled={locked} onClick={() => onPay(null)}>
          Ödemedi, sonra ödeyecek
          {plan.creditTL > 0 && <span className="muted"> · kredi kadarı düşer</span>}
        </button>
      ) : (
        // Kept in place while the field is open, so nothing moves.
        <button className="pay-link" disabled={locked} onClick={startEditing}
          style={editing ? {visibility: "hidden"} : undefined}>
          Ek ödeme al
        </button>
      )}
    </Sheet>
  );
}

// ── Geri al / kanal düzelt ────────────────────────────────
// The confirm behind the drawer's "↩ Geri al" and "⇄ Nakit'e çevir":
// one line saying what changes, one button. A tahsilat with a makbuz
// is refused by the door, with the reason.

export function UndoRecordSheet({cell, client, providerName, busy, onClose, onUndo}: {
  cell: BoardCell; client: BoardClient; providerName: string; busy: string | null;
  onClose: () => void; onUndo: () => void;
}) {
  const {tl} = useMoney();
  const paidTL = client.recorded?.paidTL ?? 0;
  const running = busy === `undo:${client.clientId}`;
  return (
    <Sheet title={<>Geri al · {cellTitle(cell, providerName)}</>} onClose={onClose}>
      <p>
        <b>{client.name}</b>: seans <b>yapılmadı, ödenmedi</b> olarak geri döner.
        {paidTL > 0 && <> Bu seansla alınan {tl(paidTL)} kayıttan silinir; krediden
          düşülen tutar krediye geri döner.</>}
      </p>
      <div className="sheet-actions">
        <button className="big" onClick={onClose}>Vazgeç</button>
        <button className="danger big" disabled={running} onClick={onUndo}>
          {running ? "Geri alınıyor…" : "Geri al"}
        </button>
      </div>
    </Sheet>
  );
}

export function ChannelSheet({cell, client, providerName, to, busy, onClose, onChange}: {
  cell: BoardCell; client: BoardClient; providerName: string; to: "cash" | "bank";
  busy: string | null; onClose: () => void; onChange: () => void;
}) {
  const {tl} = useMoney();
  const from = to === "cash" ? "havale" : "nakit";
  const word = to === "cash" ? "nakit" : "havale";
  const running = busy === `channel:${client.clientId}`;
  return (
    <Sheet title={<>Ödeme kanalı · {cellTitle(cell, providerName)}</>} onClose={onClose}>
      <p>
        <b>{client.name}</b>: {tl(client.recorded?.paidTL ?? 0)} <b>{from}</b> değil,{" "}
        <b>{word}</b> alındı. Tutar ve tarih aynı kalır.
      </p>
      <div className="sheet-actions">
        <button className="big" onClick={onClose}>Vazgeç</button>
        <button className="primary big" disabled={running} onClick={onChange}>
          {running ? "Düzeltiliyor…" : `${word[0].toUpperCase()}${word.slice(1)} olarak düzelt`}
        </button>
      </div>
    </Sheet>
  );
}

// ── Saati değiştir ────────────────────────────────────────
// The one-line confirm a drag drops into ("Can 16:00 → 17:00?"), and
// the way to move a seans without a mouse: a tablet or the keyboard
// opens it from the drawer and picks the time here.

const MOVE_STEPS = [-60, -30, -15, -5, 5, 15, 30, 60] as const;

export function MoveSheet({cell, providerName, initialTime, overlapsOf, busy, onClose, onMove}: {
  cell: BoardCell; providerName: string; initialTime: string;
  // The column's other seanslar a start time would share time with.
  overlapsOf: (time: string) => string[];
  busy: string | null; onClose: () => void; onMove: (time: string) => void;
}) {
  const [time, setTime] = useState(initialTime);
  const duration = cell.durationMin;
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const fromMin = (m: number) =>
    `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  const shift = (d: number) =>
    setTime(fromMin(Math.max(0, Math.min(24 * 60 - duration, toMin(time) + d))));
  const names = cell.clients.map((c) => c.name).join(", ") || cell.title;
  const same = time === cell.time;
  const overlaps = overlapsOf(time);
  const confirmedFor = cell.clients.some((c) => c.attendance.state === "confirmed");
  const running = busy === `move:${cell.id}`;
  return (
    <Sheet title={<>Saati değiştir · {cellTitle(cell, providerName)}</>} onClose={onClose}>
      <p className="move-line">
        <b>{names}</b> {cell.time} → <b>{time}</b>–{fromMin(toMin(time) + duration)}
        {same && <span className="muted"> (aynı saat)</span>}
      </p>
      <div className="choice-row" aria-label="Yeni saat">
        {MOVE_STEPS.map((d) => (
          <button key={d} className="choice" onClick={() => shift(d)}>
            {d > 0 ? `+${d}` : d} dk
          </button>
        ))}
        <input className="text time-input" type="time" step={300} value={time}
          aria-label="Saat" onChange={(e) => e.target.value && setTime(e.target.value)} />
      </div>
      {overlaps.length > 0 && (
        <p className="banner warn">Çakışma: {overlaps.join(" · ")} — ikisi de yerinde kalır.</p>
      )}
      {confirmedFor && (
        <p className="muted small">
          📅✔️ Veli {cell.time} için onay vermişti; takvimdeki işaret o saate aittir —
          yeni saat için çekmeceden "WhatsApp onay" yeniden gönderilebilir.
        </p>
      )}
      <p className="muted small">
        Yalnız bu günün seansı taşınır; seri ve diğer haftalar aynı kalır. Danışman
        değişikliği sohbetten.
      </p>
      <div className="sheet-actions">
        <button className="big" onClick={onClose}>Vazgeç</button>
        <button className="primary big" disabled={same || running} onClick={() => onMove(time)}>
          {running ? "Taşınıyor…" : `Taşı — ${cell.time} → ${time}`}
        </button>
      </div>
    </Sheet>
  );
}

// ── Not düş ───────────────────────────────────────────────

export function NoteSheet({client, busy, onClose, onNote}: {
  client: ClientRef; busy: string | null;
  onClose: () => void; onNote: (text: string, pin: boolean) => void;
}) {
  const [text, setText] = useState("");
  const [pin, setPin] = useState(true);
  const running = busy === `note:${client.clientId}`;
  return (
    <Sheet title={<>Not düş · {client.name}</>} onClose={onClose}>
      {client.deskNote && (
        <p className="muted small">Hücredeki not: 📝 {client.deskNote} — yenisi onun yerine geçer.</p>
      )}
      <textarea className="text" autoFocus rows={3} maxLength={500}
        placeholder="Örn. veli 10 dk geç gelecek" value={text}
        onChange={(e) => setText(e.target.value)} />
      <label className="check">
        <input type="checkbox" checked={pin} onChange={(e) => setPin(e.target.checked)} />
        Hücrede göster (masa notu — kaldırılana kadar bu danışanın her seansında)
      </label>
      <div className="sheet-actions">
        <button className="primary big" disabled={!text.trim() || running}
          onClick={() => onNote(text.trim(), pin)}>
          {running ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </Sheet>
  );
}

// ── Duplicate confirm ─────────────────────────────────────

export function DuplicateDialog({ask, onClose}: {ask: DuplicateAsk; onClose: () => void}) {
  return (
    <Sheet title="Benzer bir kayıt zaten var" onClose={onClose}>
      <p>Journal'da şuna benzeyen bir kayıt var:</p>
      <ul>
        {ask.suspects.map((s) => <li key={s.eventId}><code>{s.eventId}</code> — {s.summary}</li>)}
      </ul>
      <p className="muted small">
        Bu gerçekten ikinci, ayrı bir seans ise yine de kaydedin; aynı seans ise vazgeçin.
      </p>
      <div className="sheet-actions">
        <button className="big" onClick={onClose}>Vazgeç</button>
        <button className="danger big" onClick={() => void ask.retry()}>Yine de kaydet</button>
      </div>
    </Sheet>
  );
}
